"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "../lib/cn";

const buttonVariants = cva(
  "box-border inline-flex shrink-0 appearance-none cursor-pointer items-center justify-center gap-2 border-0 bg-transparent font-sans font-medium whitespace-nowrap transition-all outline-none hover:[box-shadow:var(--ui-button-hover-shadow,none)] hover:[transform:var(--ui-button-hover-transform,none)] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "[border-style:solid] [border-width:var(--ui-button-default-border-width,0px)] [border-color:var(--ui-button-default-border,var(--color-primary))] [background:var(--ui-button-default-background,var(--color-primary))] text-[var(--ui-button-default-foreground,var(--color-primary-foreground))] hover:[border-color:var(--ui-button-default-hover-border,var(--ui-button-default-border,var(--color-primary)))] hover:[background:var(--ui-button-default-hover-background,color-mix(in_srgb,var(--color-primary)_90%,transparent))]",
        primary:
          "[background:var(--ui-button-primary-background,var(--color-primary))] text-primary-foreground [box-shadow:var(--ui-button-primary-shadow,none)] hover:[background:var(--ui-button-primary-hover-background,color-mix(in_srgb,var(--color-primary)_90%,transparent))] hover:[box-shadow:var(--ui-button-primary-hover-shadow,var(--ui-button-primary-shadow,none))] hover:[filter:var(--ui-button-primary-hover-filter,none)]",
        destructive:
          "[border-style:solid] [border-width:var(--ui-button-destructive-border-width,0px)] [border-color:var(--ui-button-destructive-border,var(--color-destructive))] [background:var(--ui-button-destructive-background,var(--color-destructive))] [color:var(--ui-button-destructive-foreground,white)] hover:[border-color:var(--ui-button-destructive-hover-border,var(--ui-button-destructive-border,var(--color-destructive)))] hover:[background:var(--ui-button-destructive-hover-background,color-mix(in_srgb,var(--color-destructive)_90%,transparent))] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        success:
          "border border-success/30 bg-success/10 text-success hover:border-success hover:bg-success/20",
        outline:
          "border border-border [background:var(--ui-button-outline-background,var(--color-background))] [color:var(--ui-button-outline-foreground,var(--color-foreground))] hover:bg-accent hover:text-accent-foreground dark:border-input",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "[height:var(--ui-button-height,2.25rem)] [padding-inline:var(--ui-button-padding-x,1rem)] [border-radius:var(--ui-button-radius,var(--radius-md,0.375rem))] [font-size:var(--ui-button-font-size,0.875rem)]",
        md: "[height:var(--ui-button-height,2.25rem)] [padding-inline:var(--ui-button-padding-x,1rem)] [border-radius:var(--ui-button-radius,var(--radius-md,0.375rem))] [font-size:var(--ui-button-font-size,0.875rem)]",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "gap-1.5 [height:var(--ui-button-sm-height,2rem)] [padding-inline:var(--ui-button-sm-padding-x,0.75rem)] [border-radius:var(--ui-button-sm-radius,var(--radius-md,0.375rem))] [font-size:var(--ui-button-sm-font-size,0.875rem)]",
        toolbar:
          "gap-1.5 [height:var(--ui-button-height,2.25rem)] [padding-inline:var(--ui-button-sm-padding-x,0.75rem)] [border-radius:var(--ui-button-sm-radius,var(--radius-md,0.375rem))] [font-size:var(--ui-button-sm-font-size,0.875rem)]",
        lg: "h-10 rounded-md px-6 text-sm has-[>svg]:px-4",
        icon: "size-9 rounded-md text-sm",
        "icon-xs":
          "size-6 rounded-md text-xs [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 rounded-md text-sm",
        "icon-lg": "size-10 rounded-md text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    icon?: boolean;
    loading?: boolean;
  };

type ButtonVariant = NonNullable<ButtonProps["variant"]>;

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  icon = false,
  loading = false,
  disabled,
  type,
  onClick,
  ...props
}: ButtonProps) {
  const iconSize = icon
    ? size === "sm"
      ? "icon-sm"
      : size === "xs"
        ? "icon-xs"
        : "icon"
    : size;

  const isDisabled = disabled || loading;
  const sharedProps = {
    "data-slot": "button",
    "data-variant": variant,
    "data-size": iconSize,
    className: cn(buttonVariants({ variant, size: iconSize, className })),
    "aria-busy": loading || undefined,
  } as const;

  if (asChild) {
    return (
      <Slot.Root
        {...props}
        {...sharedProps}
        aria-disabled={isDisabled || undefined}
        tabIndex={isDisabled ? -1 : props.tabIndex}
        onClick={(event) => {
          if (isDisabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onClick?.(event as React.MouseEvent<HTMLButtonElement>);
        }}
      />
    );
  }

  return (
    <button
      {...sharedProps}
      disabled={isDisabled}
      type={type ?? "button"}
      onClick={onClick}
      {...props}
    />
  );
}

export { Button, buttonVariants };
export type { ButtonProps, ButtonVariant };
